import _ from "lodash";
import React, { useMemo, useRef, useState, useEffect } from "react";
import { useI18n } from "Hooks";
import ACLStore from "lib/aclStore";
import { vs } from "react-syntax-highlighter/dist/esm/styles/hljs";

// components
import MarkdownView from "UIComponents/MarkdownView";
import ClickableHighlightedText from "UIComponents/ClickableHighlightedText";
import AIAnalysisCriteriaModal from "./components/AIAnalysisCriteriaModal";
import AIFeedbackStrip from "AppComponents/AIFeedbackStrip";

// design system
import {
  InformationOutlined,
  ExpandArrowsTwoOutlined,
  RefreshArrowOutlined,
  CaretOpenedFilled,
} from "@toddle-edu/ds-icons";
import { Button, IconButton, Tag } from "@toddle-edu/ds-web";
import { Popover, ArrowContainer } from "@toddle-edu/react-tiny-popover";
import { elevations } from "@toddle-edu/ds-theme";

// modules
import {
  getAIAnalysisScaleDetailsQuery,
  getAIAnalysisScaleEvaluationQuery,
} from "./modules/AITutorAnalysisQueries";

// utils
import { getAIAnalysisItem } from "ClassRoom/modules/utils";
import { getStyleStrippedText, scrollAndHighlightBackground } from "Utils";
import {
  AI_ANALYSIS_SCALE_MAPPING,
  AI_TRANSLATION_LANGUAGES,
  getAITutorMessagesForEvaluation,
} from "@toddle-edu/common-utils";
import { getIsAITutorModalitiesEnabled } from "Utils/ACLStoreUtils";
import { getAITutorMessagesForEvaluationV2 } from "Utils/AIUtils";

// third party
import { graphql, compose } from "lib/apolloClient";
import PropTypes from "prop-types";
import classNames from "classnames";
import { connect } from "react-redux";
import hexToRgba from "hex-to-rgba";

// string constants
import { STREAMING } from "Constants/stringConstants";

// scss
import classes from "./AITutorAnalysis.scss";

const arrowSize = 10;

const styles = {
  arrowContainer: {
    padding: 0,
  },
  aiFeedbackStrip: {
    background: "var(--surface-primary-enabled)",
    padding: 0,
    borderRadius: "8px",
  },
  rotateBy90: { transform: "rotate(90deg)" },
  informationIcon: { color: "var(--tag-foreground-pink)" },
};

const InformationIconWithPopover = props => {
  const { description, containerRef } = props;

  const [isPopoverOpen, setIsPopoverOpen] = useState(false);

  const closePopover = () => setIsPopoverOpen(false);
  const openPopover = () => setIsPopoverOpen(true);

  const getArrowStyle = (position, arrowSize = 10) => {
    const positionShift = 1.5; // to expose enough of the background
    const border = `${arrowSize}px solid var(--surface-primary-enabled)`;

    switch (position) {
      case "left":
        return {
          right: positionShift,
          borderLeft: border,
        };
      case "right":
        return {
          left: positionShift,
          borderRight: border,
        };
      case "bottom":
        return {
          top: positionShift,
          borderBottom: border,
        };
      default:
        return {
          bottom: positionShift,
          borderTop: border,
        };
    }
  };

  return (
    <Popover
      isOpen={isPopoverOpen}
      positions={["bottom"]}
      onClickOutside={closePopover}
      padding={4}
      containerParent={containerRef.current}
      content={({ position, childRect, popoverRect }) => (
        <ArrowContainer
          position={position}
          childRect={childRect}
          popoverRect={popoverRect}
          arrowSize={arrowSize}
          arrowColor="var(--border-secondary)"
        >
          <ArrowContainer
            position={position}
            childRect={childRect}
            popoverRect={popoverRect}
            arrowSize={arrowSize}
            style={styles.arrowContainer}
            arrowStyle={getArrowStyle(position, arrowSize)}
          >
            <div
              className={classes.contentContainer}
              style={elevations["elevation-2B"]}
            >
              <div
                className={"text-body"}
                dangerouslySetInnerHTML={{
                  __html: getStyleStrippedText({
                    text: description,
                    type: "SANITIZE_DANGER_HTML_PARAGRAPH",
                  }),
                }}
              />
            </div>
          </ArrowContainer>
        </ArrowContainer>
      )}
    >
      <div
        className={classes.informationIconContainer}
        onFocus={openPopover}
        onBlur={closePopover}
        onMouseEnter={openPopover}
        onMouseLeave={closePopover}
        tabIndex={0}
        role={"button"}
        aria-label={"Analysis scale description"}
      >
        <InformationOutlined size="xxx-small" variant="subtle" />
      </div>
    </Popover>
  );
};

const AITutorAnalysis = props => {
  const {
    title,
    innerContainerStyle,
    evaluationContainerStyle,
    showLoader = false,
    onClickStartNewSession,
    criteria,
    indicators,
    ratedCriterion,
    messages,
    analysisToolEvaluation,
    conversationMetadata,
    currentAIState,
    isFromClassRoom,
    isFromActivityLog,
    isPreviewChat,
    source,
    isStartNewChatDisabled = false,
    tutorCreationSelectedLanguage,
    testId,
    onRegenerateClick,
    hasSelectedModality,
    showRegenerateEvaluationButton,
    onToggleAnalysisContainer = _.noop,
  } = props;

  const { t } = useI18n();

  const [showAnalysisCriteriaModal, setShowAnalysisCriteriaModal] = useState(
    false
  );

  const containerRef = useRef(null);

  const isAITutorModalitiesEnabled = getIsAITutorModalitiesEnabled();

  const codeThemeConfig = useMemo(
    () => ({
      theme: vs,
      isDarkTheme: false,
      shouldRemoveSyntaxHighlighterStyle: true,
    }),
    []
  );

  // when evaluation is getting generated then scroll to bottom
  useEffect(() => {
    if (currentAIState === STREAMING) {
      const container = containerRef.current;

      if (container) {
        container.scrollTo({
          top: container.scrollHeight,
          behavior: "smooth",
        });
      }
    }
  }, [currentAIState, analysisToolEvaluation]);

  const { label: proficiencyLevel, metadata } = ratedCriterion || {};
  const { colorCode } = metadata || {};

  const { tagVariant, color = "" } =
    _.find(AI_ANALYSIS_SCALE_MAPPING, { id: colorCode }) || {};
  const backgroundColor = hexToRgba(color, 0.2);

  const loadingTextClass = classNames("text-body", classes.loadingText);

  const submissionPendingContainerClass = classNames({
    "text-body": true,
    [classes.submissionPendingContainer]: true,
  });

  const headerTitleContainerClasss = classNames({
    [classes.headerTitleContainer]: true,
    [classes.headerTitleContainerWithoutPadding]: source === "PREVIEW_CHAT",
  });

  const toggleAnalysisCriteriaModal = () => {
    setShowAnalysisCriteriaModal(prevState => !prevState);
  };

  const showFeedbackStrip =
    !_.isEmpty(analysisToolEvaluation) && !isPreviewChat;

  const titleComp = (
    <div className={headerTitleContainerClasss}>
      <div className={classes.titleWithFeedback}>
        <div className="text-label">{title ? title : t("ai:ai_analysis")}</div>

        {showFeedbackStrip ? (
          <AIFeedbackStrip
            showWarningText={false}
            containerStyle={styles.aiFeedbackStrip}
            {...conversationMetadata}
            showLikeButton={false}
            showDislikeButton={false}
            sourceFrom={"LESSON_PLANNING_ASSISTANT"}
          />
        ) : null}
      </div>

      <div className={classes.tagWithExpandedIcon}>
        {showRegenerateEvaluationButton && _.isFunction(onRegenerateClick) ? (
          <IconButton
            dsVersion="2.0"
            size="small"
            icon={<RefreshArrowOutlined />}
            variant="neutral"
            type="plain"
            onClick={onRegenerateClick}
            testId={`${testId}-regenerate`}
            disabled={!_.isEmpty(currentAIState)}
          />
        ) : null}

        {!_.isEmpty(proficiencyLevel) ? (
          <Tag
            dsVersion={"2.0"}
            size={"medium"}
            color={tagVariant}
            testId={`${testId}-proficiencyLevelTag`}
            onClick={toggleAnalysisCriteriaModal}
          >
            <div className={classes.proficiencyLevelTagCon}>
              <span
                dangerouslySetInnerHTML={{
                  __html: getStyleStrippedText({
                    text: proficiencyLevel,
                    type: "SANITIZE_DANGER_HTML_PARAGRAPH",
                  }),
                }}
              />

              {isAITutorModalitiesEnabled ? (
                <InformationOutlined
                  size="xxx-small"
                  style={{ color: `var(--tag-foreground-${tagVariant})` }}
                />
              ) : null}
            </div>
          </Tag>
        ) : null}

        {source !== "PREVIEW_CHAT" && !isAITutorModalitiesEnabled ? (
          <IconButton
            dsVersion="2.0"
            size="small"
            icon={<ExpandArrowsTwoOutlined />}
            variant="neutral"
            type="plain"
            onClick={toggleAnalysisCriteriaModal}
            testId={`${testId}-expandIcon`}
          />
        ) : null}

        {isAITutorModalitiesEnabled && hasSelectedModality ? (
          <IconButton
            dsVersion="2.0"
            size="small"
            variant="neutral"
            type="plain"
            icon={<CaretOpenedFilled style={styles.rotateBy90} />}
            onClick={onToggleAnalysisContainer}
            testId={`${testId}-collapse`}
          />
        ) : null}
      </div>
    </div>
  );

  const renderAnalysisScale = useMemo(() => {
    switch (source) {
      case "CLASSROOM":
      case "ACTIVITY_LOG":
        return (
          <div className={classes.proficiencyLevelsContainer}>
            <div className={classes.titleWithExpandedIcon}>
              <div className={classes.titleWithInfoIcon}>
                <div className="text-label-l">
                  {title ? title : t("ai:ai_analysis")}
                </div>

                {isAITutorModalitiesEnabled ? (
                  <IconButton
                    dsVersion="2.0"
                    icon={<InformationOutlined />}
                    size="small"
                    variant="neutral"
                    type="plain"
                    onClick={toggleAnalysisCriteriaModal}
                  />
                ) : null}

                {showFeedbackStrip ? (
                  <AIFeedbackStrip
                    showWarningText={false}
                    containerStyle={styles.aiFeedbackStrip}
                    {...conversationMetadata}
                    showLikeButton={false}
                    showDislikeButton={false}
                  />
                ) : null}
              </div>

              <IconButton
                dsVersion="2.0"
                size="small"
                icon={<ExpandArrowsTwoOutlined />}
                variant="neutral"
                type="plain"
                onClick={toggleAnalysisCriteriaModal}
                testId={`${testId}-expandIcon-2`}
              />
            </div>

            <div className={classes.proficiencyLevels}>
              {_.map(criteria, criterion => {
                const { id, label, description } = criterion;

                const isHighlightedCard = proficiencyLevel === label;

                const cardContainerStyle = {
                  backgroundColor: isHighlightedCard
                    ? backgroundColor
                    : "var(--surface-secondary-enabled",
                  border: isHighlightedCard
                    ? `1px solid ${color}`
                    : "1px solid var(--border-secondary)",
                };

                return (
                  <div
                    key={id}
                    style={cardContainerStyle}
                    className={classes.proficiencyLevelCard}
                  >
                    <div className={classes.proficiencyLevelTag}>
                      <div
                        className="text-label"
                        dangerouslySetInnerHTML={{
                          __html: getStyleStrippedText({
                            text: label,
                            type: "SANITIZE_DANGER_HTML_PARAGRAPH",
                          }),
                        }}
                      />
                      {description && !isAITutorModalitiesEnabled ? (
                        <InformationIconWithPopover
                          containerRef={containerRef}
                          description={description}
                        />
                      ) : null}
                    </div>
                    {isHighlightedCard ? (
                      <div
                        className={classes.badgeDot}
                        style={{ backgroundColor: color }}
                      />
                    ) : null}
                  </div>
                );
              })}
            </div>
          </div>
        );
      default:
        return titleComp;
    }
  }, [source, titleComp]);

  const onClickHighlightedText = args => {
    const { messageids, messageIds } = args;
    const ids = messageIds || messageids;

    const idIndex = _.toNumber(
      _.first(_.split(_.replace(ids, /[\[\]\s]/g, ""), ","))
    );

    const message = _.get(messages, `${idIndex}.node`);
    const { authorType, attachments, metadata } = message || {};

    let messageId = _.get(message, "id");

    // we need to highlight user messages only but if AI does not output correctly and give AI message to highlight
    // then we'll highlight the previous user message
    if (authorType === "assistant") {
      messageId = _.findLast(_.slice(messages, 0, idIndex), {
        node: { authorType: "user" },
      })?.node?.id;
    }

    // this is to  highlight media card if content is empty
    const highlightChild =
      isAITutorModalitiesEnabled &&
      (_.has(metadata, "audioUrls") ||
        _.has(metadata, "imageUrls") ||
        !_.isEmpty(attachments));

    scrollAndHighlightBackground({
      id: `${messageId}_AI_MESSAGE`,
      className: classes.blinkBackground,
      highlightChild,
    });
  };

  const evaluationPendingMessage = isPreviewChat
    ? null
    : isFromClassRoom
    ? t("ai:ai_tutor_student_submission_pending_msg")
    : t("ai:ai_tutor_evaluation_pending_msg");

  const descriptionContainerClass = classNames({
    [classes.descriptionContainer]: true,
    [classes.descriptionContainerWithPaddingBottom]:
      isFromClassRoom || isFromActivityLog,
  });

  return (
    <div className={classes.container} ref={containerRef}>
      <div className={classes.innerContainer} style={innerContainerStyle}>
        {renderAnalysisScale}

        {!_.isEmpty(analysisToolEvaluation) ? (
          <div
            className={descriptionContainerClass}
            style={evaluationContainerStyle}
          >
            {_.map(analysisToolEvaluation, evaluation => {
              const { id, columnId, subjectiveResponse } = evaluation || {};

              let headerLabel = _.get(
                _.find(indicators, { id: columnId }),
                "label"
              );

              const localisedContent = _.find(AI_TRANSLATION_LANGUAGES, {
                code: tutorCreationSelectedLanguage,
              })?.localisedContent;

              if (localisedContent?.[headerLabel]) {
                headerLabel = localisedContent[headerLabel];
              } else {
                headerLabel = _.includes(headerLabel, "ai:") // to make things backward compatible
                  ? t(headerLabel)
                  : headerLabel;
              }

              return (
                <div key={id} className={classes.section}>
                  {headerLabel ? (
                    <div className="text-label">{headerLabel}</div>
                  ) : null}

                  <MarkdownView
                    contentContainerClass={classes.blockContentText}
                    content={subjectiveResponse}
                    components={{
                      annt: params => {
                        const { children } = params;
                        return (
                          <ClickableHighlightedText
                            onClick={onClickHighlightedText}
                            {...params}
                          >
                            {children}
                          </ClickableHighlightedText>
                        );
                      },
                    }}
                    enableHighlightedText={true}
                    enableHTMLrendering={true}
                    enableLatexSanitization={true}
                    codeThemeConfig={codeThemeConfig}
                  />
                </div>
              );
            })}
          </div>
        ) : showLoader ? (
          <div className={classes.loadingContainer}>
            <div className={classes.dot} />
            <div className={loadingTextClass}>
              {t("ai:analysing_this_conversation")}
            </div>
          </div>
        ) : evaluationPendingMessage ? (
          <div className={submissionPendingContainerClass}>
            {evaluationPendingMessage}
          </div>
        ) : null}
      </div>

      {onClickStartNewSession &&
      !_.isEmpty(analysisToolEvaluation) &&
      _.isEmpty(currentAIState) ? (
        <div className={classes.buttonWrapper}>
          <Button
            dsVersion={"2.0"}
            variant={"neutral"}
            type={isAITutorModalitiesEnabled ? "fill" : "outlined"}
            size={"medium"}
            onClick={onClickStartNewSession}
            isFullWidth={true}
            disabled={isStartNewChatDisabled}
            testId={`${testId}-startNewChatButton`}
          >
            {t("ai:start_a_new_chat")}
          </Button>
        </div>
      ) : null}

      {showAnalysisCriteriaModal ? (
        <AIAnalysisCriteriaModal
          t={t}
          onClose={toggleAnalysisCriteriaModal}
          analysisCriteria={criteria}
        />
      ) : null}
    </div>
  );
};

AITutorAnalysis.propTypes = {
  title: PropTypes.string,
  conversationId: PropTypes.string,
  onClickStartNewSession: PropTypes.func,
  containerStyle: PropTypes.object,
};

AITutorAnalysis.defaultProps = {
  testId: "assessment-aiTutorAnalysis",
};

const mapStateToProps = (state, ownProps) => {
  const { source } = ownProps;

  const isFromClassRoom = source === "CLASSROOM";
  const isPreviewChat = source === "PREVIEW_CHAT";
  const isFromActivityLog = source === "ACTIVITY_LOG";

  const showRegenerateEvaluationButton =
    ACLStore.can("IdentityAccess:ShowEditIconInAITutor") ||
    ACLStore.can("Common:ShowEditIconInAITutor");

  return {
    isFromClassRoom,
    isFromActivityLog,
    isPreviewChat,
    showRegenerateEvaluationButton,
    isData: true,
    isLoading: false,
  };
};

export default compose(
  connect(mapStateToProps, null),
  graphql(getAIAnalysisScaleDetailsQuery, {
    name: "getAIAnalysisScaleDetails",
    skip: ({ assessmentId }) => _.isEmpty(assessmentId),
    options: ({ assessmentId }) => ({
      fetchPolicy: "cache-first",
      variables: { id: assessmentId },
      context: {
        useHttpLink: true,
      },
    }),
    props({ getAIAnalysisScaleDetails, ownProps: { isData, isLoading } }) {
      const { node, networkStatus } = getAIAnalysisScaleDetails;

      const criteria = getAIAnalysisItem(node);
      const indicators = getAIAnalysisItem(node, "indicators");

      // find the language from the last draft message
      // evaluation headers (strengths, areas of improvement, next steps) will be shown in this language
      const taskCreationConversation = _.find(
        _.get(node, "aiConversation.edges", []),
        ({ node }) => {
          return node?.entitySubType === "TASK_CREATION";
        }
      );

      const draftMessage = _.find(
        _.get(taskCreationConversation, "node.messages.edges"),
        ({ node }) => {
          return _.get(node, "metadata.isDraftTaskMessage", false);
        }
      );

      let tutorCreationSelectedLanguage = _.get(
        draftMessage,
        "node.metadata.language"
      );

      // if no draft message is found (chat cleared), then we can get the language from the task creation conversation
      if (!tutorCreationSelectedLanguage) {
        tutorCreationSelectedLanguage = _.chain(node)
          .get("aiConversation.edges", [])
          .find({ node: { entitySubType: "TASK_CREATION" } })
          .get("node.metadata.languageCode")
          .value();
      }

      return {
        criteria,
        indicators,
        tutorCreationSelectedLanguage,
        isData: isData && !_.isEmpty(node),
        isLoading: _.includes([1, 2], networkStatus) || isLoading,
      };
    },
  }),
  graphql(getAIAnalysisScaleEvaluationQuery, {
    name: "getAIAnalysisScaleEvaluation",
    skip: ({ aiConversationId, skipEvaluationQuery = false }) =>
      _.isEmpty(aiConversationId) || skipEvaluationQuery,
    options: ({ aiConversationId }) => ({
      fetchPolicy: "cache-first",
      variables: { id: aiConversationId },
      context: {
        useHttpLink: true,
      },
    }),
    props({
      getAIAnalysisScaleEvaluation,
      ownProps: { isData, isLoading, criteria, indicators },
    }) {
      const { node, networkStatus } = getAIAnalysisScaleEvaluation;

      const indicatorsDisplaySequenceMap = _.keyBy(indicators, "id");

      const messageEdges = _.get(node, "messages.edges");

      let analysisToolEvaluation = _.get(node, "analysisToolEvaluation", []);
      const conversationMetadata = _.get(node, "metadata", {});

      analysisToolEvaluation = _.find(analysisToolEvaluation, {
        toolType: "ANALYSIS",
      })?.toolResponses;

      analysisToolEvaluation = _.sortBy(
        analysisToolEvaluation,
        response =>
          indicatorsDisplaySequenceMap?.[response?.columnId]?.displaySequence
      );

      const ratedCriterion = _.find(criteria, criterion => {
        return _.first(analysisToolEvaluation)?.rowId === criterion?.id;
      });

      const messages = getIsAITutorModalitiesEnabled()
        ? getAITutorMessagesForEvaluationV2(messageEdges)
        : getAITutorMessagesForEvaluation(messageEdges);

      return {
        messages,
        conversationMetadata,
        ratedCriterion,
        analysisToolEvaluation,
        isData: isData && !_.isEmpty(node),
        isLoading: _.includes([1, 2], networkStatus) || isLoading,
      };
    },
  })
)(AITutorAnalysis);
